import React from 'react';
import { connect } from 'react-redux';
import { createActions } from '../../../store';
import { MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, GRADIENT_TYPE, BACKGROUND_COLOR_DEFAULTS, DEFAULT_BACKGROUND_IMAGE_URL } from '../../../CreatePageConstants';
import { createMediaSource, createMediaSourceDataForImage } from '../../../helpers';
import ColorPickerListItem from '../../../../../list-items/ColorPickerListItem';
import SelectListItem from '../../../../../list-items/SelectListItem/SelectListItem';
import SliderListItem from '../../../../../list-items/SliderListItem/SliderListItem';

// UI-only background mode for a single solid color (gradients use GRADIENT_TYPE values).
const SOLID_COLOR_MODE = 'color';
const IMAGE_MODE = 'image';

const MODE_OPTIONS = [
    { value: IMAGE_MODE, text: 'Media' },
    { value: SOLID_COLOR_MODE, text: 'Solid Color' },
    { value: GRADIENT_TYPE.LINEAR, text: 'Linear Gradient' },
    { value: GRADIENT_TYPE.RADIAL, text: 'Radial Gradient' }
];

const isColorOrGradient = (mediaSource) =>
    mediaSource?.mediaResourceType === MEDIA_RESOURCE_TYPE.COLOR ||
    mediaSource?.mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT;

const BackdropColorListItem = ({ mediaSource, updateBackgroundSettings }) => {
    const isActive = isColorOrGradient(mediaSource);
    const isGradientResource = mediaSource?.mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT;

    const colorSettings = (isActive && mediaSource.colorSettings) || {};
    const gradientSettings = (isActive && mediaSource.gradientSettings) || {};
    const gradientColors = gradientSettings.colors ?? [];

    // The first gradient color stop doubles as the solid color when not in gradient mode.
    const color = (isGradientResource ? gradientColors[0] : colorSettings.color) ?? BACKGROUND_COLOR_DEFAULTS.color;
    const secondaryColor = gradientColors[1] ?? BACKGROUND_COLOR_DEFAULTS.color2;
    const gradientType = gradientSettings.type ?? BACKGROUND_COLOR_DEFAULTS.gradientType;
    const gradientAngle = gradientSettings.angle ?? BACKGROUND_COLOR_DEFAULTS.gradientAngle;
    const gradientRadius = gradientSettings.radius ?? BACKGROUND_COLOR_DEFAULTS.gradientRadius;

    const currentMode = isActive ? (isGradientResource ? gradientType : SOLID_COLOR_MODE) : IMAGE_MODE;
    const isGradient = currentMode === GRADIENT_TYPE.LINEAR || currentMode === GRADIENT_TYPE.RADIAL;

    // Builds a fresh color/gradient mediaSource. createMediaSource supplies the shared
    // fields (operationStamp, mediaResourceType); the typed settings object replaces the
    // legacy `data` dictionary that solid colors and gradients used to carry. A picked color is the
    // user's own background rather than a preset one, so applying a preset leaves it in place.
    const persistMediaSource = (mediaResourceType, settings) => {
        const base = createMediaSource(null, MEDIA_SOURCE_TYPE.SPECTERR_CDN, null, mediaResourceType);
        updateBackgroundSettings({ mediaSource: { ...base, ...settings, isPreset: false } });
    };

    const persistColor = (nextColor) => {
        persistMediaSource(MEDIA_RESOURCE_TYPE.COLOR, { colorSettings: { color: nextColor } });
    };

    // Merge a partial change into the current gradient settings and persist it, seeding
    // from the resolved values so the gradient always carries a complete payload.
    const persistGradient = (patch) => {
        const mergedGradient = {
            colors: [color, secondaryColor],
            type: gradientType,
            angle: gradientAngle,
            radius: gradientRadius,
            ...patch
        };

        // Prevent infinite update loops caused by unmemoized onChange handlers (e.g. in SliderListItem)
        if (
            mediaSource?.mediaResourceType === MEDIA_RESOURCE_TYPE.GRADIENT &&
            mergedGradient.colors[0] === color &&
            mergedGradient.colors[1] === secondaryColor &&
            mergedGradient.type === gradientType &&
            mergedGradient.angle === gradientAngle &&
            mergedGradient.radius === gradientRadius
        ) {
            return;
        }

        persistMediaSource(MEDIA_RESOURCE_TYPE.GRADIENT, { gradientSettings: mergedGradient });
    };

    const onModeChange = (event) => {
        const mode = event.target.value;
        if (mode === IMAGE_MODE) {
            const defaultBackground = createMediaSource(
                createMediaSourceDataForImage(DEFAULT_BACKGROUND_IMAGE_URL),
                MEDIA_SOURCE_TYPE.SPECTERR_CDN,
                null,
                MEDIA_RESOURCE_TYPE.IMAGE
            );
            updateBackgroundSettings({ mediaSource: { ...defaultBackground, isPreset: true } });
        } else if (mode === SOLID_COLOR_MODE) {
            persistColor(color);
        } else {
            persistGradient({ type: mode });
        }
    };

    return (
        <React.Fragment>
            <SelectListItem
                text="Mode"
                value={currentMode}
                onChange={onModeChange}
                options={MODE_OPTIONS}
            />

            {isActive && (
                <React.Fragment>
                    <ColorPickerListItem
                        text={isGradient ? "Color Stop 1" : "Background Color"}
                        color={color}
                        onChange={(result) => (isGradient
                            ? persistGradient({ colors: [result.hex, secondaryColor] })
                            : persistColor(result.hex))}
                        disableAlpha={true}
                    />

                    {isGradient && (
                        <ColorPickerListItem
                            text="Color Stop 2"
                            color={secondaryColor}
                            onChange={(result) => persistGradient({ colors: [color, result.hex] })}
                            disableAlpha={true}
                        />
                    )}

                    {currentMode === GRADIENT_TYPE.LINEAR && (
                        <SliderListItem
                            title="Gradient Angle"
                            value={gradientAngle}
                            onValueChange={(angle) => persistGradient({ angle })}
                            min={0}
                            max={360}
                            step={1}
                        />
                    )}

                    {currentMode === GRADIENT_TYPE.RADIAL && (
                        <SliderListItem
                            title="Gradient Radius"
                            value={gradientRadius}
                            onValueChange={(radius) => persistGradient({ radius })}
                            min={10}
                            max={200}
                            step={1}
                        />
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

const mapStateToProps = (state) => {
    const { mediaSource } = state.createPreview.present.videoSettings.background;
    return { mediaSource };
};

const mapDispatchToProps = (dispatch) => ({
    updateBackgroundSettings: (properties) => dispatch(createActions.updateBackgroundSettings(properties))
});

export default connect(mapStateToProps, mapDispatchToProps)(BackdropColorListItem);
